import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StarFilled, CloseOutlined } from '@ant-design/icons';
import {
  BannerBar,
  IconCircle,
  TextWrapper,
  SubText,
  CtaLink,
  CloseButton,
} from './GartnerReviewBanner.styled';

// Gartner Peer Insights review/referral link (sponsored €25 voucher campaign).
const GARTNER_REVIEW_URL = 'https://gtnr.io/wzLvkFldw';

// Identifies this banner/campaign in analytics. Bump when a new campaign starts
// so GA4 reporting can distinguish runs.
const BANNER_ID = 'gartner_review_v1';

// Bump the version suffix to re-show the banner to users who previously dismissed it
// (e.g. when a new campaign starts).
const DISMISS_STORAGE_KEY = 'gartnerReviewBannerDismissed.v1';

// Push a custom event to the GTM dataLayer (GTM is initialised in App.jsx).
// A GA4 tag/trigger in the GTM container forwards these events to GA4 — see the
// marketing note in the PR description. Safe no-op if GTM is not present.
const trackEvent = (event) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, banner_id: BANNER_ID });
  } catch (e) {
    // analytics must never break the UI
  }
};

const readDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const GartnerReviewBanner = () => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(readDismissed);

  // Fire an impression event once, the first time the banner is actually shown.
  useEffect(() => {
    if (!dismissed) {
      trackEvent('gartner_banner_impression');
    }
    // run only on mount; we only care about the initial visible render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    trackEvent('gartner_banner_dismiss');
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, '1');
    } catch (e) {
      // ignore storage errors (private mode etc.) – banner just won't persist.
    }
    setDismissed(true);
  };

  const handleCtaClick = () => {
    trackEvent('gartner_banner_cta_click');
  };

  if (dismissed) {
    return null;
  }

  return (
    <BannerBar
      role="region"
      aria-label={t('GARTNER_BANNER.ARIA_LABEL')}
      data-testid="gartner-banner"
    >
      <IconCircle aria-hidden="true">
        <StarFilled />
      </IconCircle>
      <TextWrapper>
        <Trans i18nKey="GARTNER_BANNER.TAGLINE" components={{ b: <strong /> }} />
        <SubText>{t('GARTNER_BANNER.SUBTITLE')}</SubText>
      </TextWrapper>
      <CtaLink
        id="gartner-banner-cta"
        data-testid="gartner-banner-cta"
        href={GARTNER_REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleCtaClick}
      >
        {t('GARTNER_BANNER.CTA')}
        {' →'}
      </CtaLink>
      <CloseButton
        type="button"
        data-testid="gartner-banner-close"
        onClick={handleClose}
        aria-label={t('GARTNER_BANNER.CLOSE')}
        title={t('GARTNER_BANNER.CLOSE')}
      >
        <CloseOutlined />
      </CloseButton>
    </BannerBar>
  );
};

export default GartnerReviewBanner;
